// App-level error boundary sitting just outside the IonRouterOutlet. If any
// route's render crashes — including the Ionic page-transition oddities
// that previously left the whole right pane blank-white — we catch it and
// render a small recovery card so the user can navigate elsewhere instead
// of staring at an empty page.
//
// Distinct from the PM-suite ViewBoundary (which sits inside ProjectsList
// and catches per-view crashes); this is the last line of defense covering
// the entire content area.

import { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

class RouteBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    // Helpful in dev when chasing the intermittent blanking; harmless in prod.
    // eslint-disable-next-line no-console
    console.error('RouteBoundary caught:', error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return (
        <div className="rr-route-boundary">
          <div className="rr-route-boundary-card">
            <h2>Something broke while loading that page</h2>
            <p>
              We caught the error so the rest of the app keeps running. Try
              navigating to another section, or refresh the page.
            </p>
            <details>
              <summary>Technical details</summary>
              <pre>{this.state.error.message}</pre>
            </details>
            <button type="button" className="rr-route-boundary-retry" onClick={this.reset}>
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default RouteBoundary;
